import { useEffect, useState, useCallback } from "react";
import { api, formatCOP, hoyISO } from "@/lib/api";
import UsageTip from "@/components/UsageTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy, Clock, Filter } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DateField from "@/components/DateField";

export default function ReportesPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [tab, setTab] = useState("comisiones");
  const today = hoyISO();
  const firstOfMonth = today.slice(0, 8) + "01";
  const [desde, setDesde] = useState(firstOfMonth);
  const [hasta, setHasta] = useState(today);
  const [comisiones, setComisiones] = useState({ resumen: [], detalle: [], total: {} });
  const [historial, setHistorial] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const loadCom = useCallback(async () => {
    const p = new URLSearchParams();
    if (desde) p.set("desde", desde);
    if (hasta) p.set("hasta", hasta);
    const { data } = await api.get(`/reportes/comisiones?${p.toString()}`);
    setComisiones(data);
  }, [desde, hasta]);

  const loadHist = useCallback(async () => {
    const { data } = await api.get("/reportes/historial-comercial");
    setHistorial(data);
  }, []);

  useEffect(() => { if (tab === "comisiones") loadCom(); else loadHist(); }, [tab, loadCom, loadHist]);

  return (
    <div>
      <header className="mb-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Análisis</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Reportes</h1>
        <p className="text-muted-foreground mt-2">
          {isDirector
            ? "Comisiones de todo el equipo y auditoría comercial."
            : "Tus comisiones del período y el detalle de cada pedido."}
        </p>
      </header>

      <div className="flex gap-1 bg-card border border-border rounded-lg p-1 w-fit mb-4">
        {[
          { k: "comisiones", label: "Comisiones", icon: Trophy },
          { k: "historial", label: "Historial de cambios", icon: Clock },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 h-9 rounded-md text-sm flex items-center gap-2 ${tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`tab-${t.k}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "comisiones" && (
        <>
          <UsageTip storageKey="reportes-comisiones" steps={[
            "Filtra por rango de fechas.",
            "Toca una fila para ver el detalle línea por línea de cada venta.",
          ]} />

          <div className="flex gap-3 mb-4 items-end flex-wrap bg-card border border-border rounded-xl p-4">
            <div>
              <Label>Desde</Label>
              <DateField value={desde} onChange={setDesde} className="w-40 mt-1" data-testid="filtro-desde" />
            </div>
            <div>
              <Label>Hasta</Label>
              <DateField value={hasta} onChange={setHasta} className="w-40 mt-1" data-testid="filtro-hasta" />
            </div>
            <div className="ml-auto grid grid-cols-3 gap-3 text-right">
              <div>
                <div className="text-[10px] uppercase font-bold text-muted-foreground">Pedidos</div>
                <div className="text-2xl font-semibold tabular-nums">{comisiones.total?.pedidos ?? 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-muted-foreground">Ventas</div>
                <div className="text-2xl font-semibold tabular-nums">{formatCOP(comisiones.total?.total_ventas ?? 0)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-primary">Comisión</div>
                <div className="text-2xl font-semibold tabular-nums text-primary">{formatCOP(comisiones.total?.total_comision ?? 0)}</div>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <div className="col-span-4">Vendedor</div>
              <div className="col-span-2 text-right">Pedidos</div>
              <div className="col-span-3 text-right">Ventas</div>
              <div className="col-span-3 text-right">Comisión</div>
            </div>
            {comisiones.resumen.length === 0 && (
              <div className="px-6 py-16 text-center text-muted-foreground/70"><Trophy className="w-8 h-8 mx-auto text-muted-foreground/50" /><div className="mt-3 text-sm">Sin ventas confirmadas en este rango.</div></div>
            )}
            {comisiones.resumen.map((r) => {
              const detalle = comisiones.detalle.filter((d) => d.vendedor_id === r.vendedor_id);
              const abierto = expanded === r.vendedor_id;
              return (
                <div key={r.vendedor_id}>
                  <div onClick={() => setExpanded(abierto ? null : r.vendedor_id)} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/60 items-center hover:bg-muted/40 cursor-pointer" data-testid={`vendedor-row-${r.vendedor_id}`}>
                    <div className="col-span-4 font-medium text-foreground">{r.vendedor_nombre}</div>
                    <div className="col-span-2 text-right tabular-nums">{r.pedidos}</div>
                    <div className="col-span-3 text-right tabular-nums">{formatCOP(r.total_ventas)}</div>
                    <div className="col-span-3 text-right tabular-nums font-semibold text-primary">{formatCOP(r.total_comision)}</div>
                  </div>
                  {abierto && detalle.length > 0 && (
                    <div className="bg-muted/40 border-b border-border/60 px-6 py-3">
                      <div className="text-[10px] uppercase font-bold text-muted-foreground mb-2">Detalle</div>
                      <div className="divide-y divide-slate-200">
                        {detalle.map((d, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 py-2 text-xs">
                            <div className="col-span-3 font-mono text-foreground/80">{d.pedido_numero}</div>
                            <div className="col-span-3 text-foreground/80">{d.producto_nombre}</div>
                            <div className="col-span-2 text-muted-foreground">{d.cliente_nombre}</div>
                            <div className="col-span-2 text-right tabular-nums">{d.cantidad} × {formatCOP(d.precio_unitario)}</div>
                            <div className="col-span-2 text-right tabular-nums text-primary font-semibold">{formatCOP(d.comision_valor)} <span className="text-[10px] text-muted-foreground">({d.comision_pct}%)</span></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "historial" && (
        <>
          <UsageTip storageKey="reportes-historial" steps={[
            "Registro inmutable de cambios comerciales: precios, costos, reglas y políticas.",
            "Muestra quién, cuándo, y el valor anterior vs nuevo.",
          ]} />
          <div className="space-y-2">
            {historial.length === 0 && <div className="py-12 text-center text-muted-foreground/70 border border-dashed border-border rounded-xl"><Clock className="w-8 h-8 mx-auto text-muted-foreground/50" /><div className="mt-2 text-sm">Sin cambios registrados.</div></div>}
            {historial.map((h) => (
              <div key={h.id} className="bg-card border border-border rounded-lg p-4" data-testid={`audit-${h.id}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="text-sm font-semibold text-foreground">{h.action.replace(/_/g, " ")}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Por <b>{h.user_name || h.user_id}</b> · {new Date(h.timestamp || h.at || h.created_at).toLocaleString("es-CO")}</div>
                  </div>
                  <div className="text-[10px] uppercase px-2 py-1 rounded bg-muted text-muted-foreground">{h.target_type}</div>
                </div>
                {(h.before || h.after) && (
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    {h.before && (
                      <div className="p-2 bg-rose-50 border border-rose-100 rounded-md">
                        <div className="text-[10px] uppercase font-bold text-rose-700 mb-1">Antes</div>
                        <pre className="text-foreground/80 whitespace-pre-wrap break-all font-mono text-[11px]">{JSON.stringify(h.before, null, 1)}</pre>
                      </div>
                    )}
                    {h.after && (
                      <div className="p-2 bg-accent border border-emerald-100 rounded-md">
                        <div className="text-[10px] uppercase font-bold text-primary mb-1">Después</div>
                        <pre className="text-foreground/80 whitespace-pre-wrap break-all font-mono text-[11px]">{JSON.stringify(h.after, null, 1)}</pre>
                      </div>
                    )}
                  </div>
                )}
                {h.metadata?.motivo && <div className="mt-2 text-xs text-muted-foreground"><b>Motivo:</b> {h.metadata.motivo}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
